import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  GridRowParams,
  GridRowClassNameParams,
} from '@mui/x-data-grid-pro';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import React, { useMemo, useState } from 'react';
import {
  HandlersUserDetailResponse,
  useDeleteApiUsersByIdMutation,
  useGetApiUsersQuery,
  usePostApiUsersMutation,
} from '../../../../../app/athenaApiGen';
import { StyledDataGridPro } from '../../../../../shared-theme/StyledComponents';
import { TABLE_PAGE_SIZE_OPTIONS } from '../../../../../utils/tableVariables';
import { Role } from '../../../Types';
import AddUserDialog from './AddUserDialog';
import DeleteUserDialog from './DeleteUserDialog';
import { getRolesString, getUserTableColumns } from './userTableColumns';

export interface UserTableProps {
  newUser: {
    userName: string;
    userEmail: string;
    roles: Array<Role>;
  };
  setNewUser: (user: {
    userName: string;
    userEmail: string;
    roles: Role[];
  }) => void;
  setParentSelectedUser: (user: HandlersUserDetailResponse) => void;
  onNotification: (message: string, severity: 'success' | 'error') => void;
}

export default function UserTable({
  newUser,
  setNewUser,
  setParentSelectedUser,
  onNotification,
}: UserTableProps) {
  const { data: usersData = [], isFetching: usersFetching } = useGetApiUsersQuery();

  // Reverse to show newest users first (API returns oldest first)
  const users = useMemo(() => [...usersData].reverse(), [usersData]);

  // Free-text filter over name + email. Case-insensitive, trimmed; uses the
  // same value-resolution as the table columns (userName falls back to
  // contact.name) so a search hit matches what the user actually sees.
  const [search, setSearch] = useState('');
  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = (u.userName || u.contact?.name || '').toLowerCase();
      const email = (u.userEmail || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [users, search]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] =
    useState<HandlersUserDetailResponse | null>(null);
  const [userToDelete, setUserToDelete] = useState<{
    user: HandlersUserDetailResponse;
    idx: number;
  } | null>(null);
  const [formData, setFormData] = useState({
    userEmail: '',
    userType: '',
    contact: {
      name: '',
      address: '',
      govEmail: '',
      personalPhone: '',
      workEmail: '',
      workPhone: '',
    },
    roles: [] as Partial<Role>[],
  });

  // Find SCITES Program Viewer role
  const getDefaultRole = () => {
    return [];
  };

  const [deleteUser] = useDeleteApiUsersByIdMutation();
  const [postUser] = usePostApiUsersMutation();

  // Column definitions with delete handler
  const columns = useMemo(
    () =>
      getUserTableColumns((user) => {
        const idx = users.findIndex((u) => u.id === user.id);
        setUserToDelete({ user, idx });
        setDeleteDialogOpen(true);
      }),
    [users]
  );

  const handleQueryError = (error: unknown, messagePrefix = 'Error') => {
    let msg = `${messagePrefix}: ${error}`;
    if ((error as FetchBaseQueryError).status) {
      const err = error as FetchBaseQueryError;
      msg = `${messagePrefix}: ${err.status} - ${JSON.stringify(err.data)}`;
    }
    console.error(msg, error);
    if (onNotification) {
      onNotification(msg, 'error');
    }
  };

  // Confirm deletion and perform API call
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    const { user } = userToDelete;

    if (user.id) {
      try {
        await deleteUser({ id: user.id }).unwrap();
        if (onNotification) {
          onNotification('User successfully deleted', 'success');
        }
      } catch (error) {
        handleQueryError(error, 'Error deleting user');
      }
    } else {
      console.log('User has no ID, cannot delete from API:', user);
      if (onNotification) {
        onNotification('User has no ID, cannot delete from API', 'error');
      }
    }

    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Add user with roles as array
  const handleAddUser = () => {
    setFormData({
      userEmail: newUser.userEmail || '',
      userType: '',
      contact: {
        name: newUser.userName || '',
        address: '',
        govEmail: '',
        personalPhone: '',
        workEmail: newUser.userEmail || '',
        workPhone: '',
      },
      roles: getDefaultRole(),
    });
    setDialogOpen(true);
  };

  // Handle form field changes
  const handleFormChange = (field: string, value: string) => {
    if (field.startsWith('contact.')) {
      const contactField = field.replace('contact.', '');
      setFormData((prev) => ({
        ...prev,
        contact: {
          ...prev.contact,
          [contactField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const prepareRolesForPayload = (
    roles: Partial<Role>[]
  ): Array<{ roleName: string; programId: string }> => {
    if (!roles) return [];
    return roles
      .map((role) => ({
        roleName: role.roleName || '',
        programId: role.programId || '',
      }))
      .filter((role) => role.roleName && role.programId);
  };

  // Handle form submission
  const handleSubmitUser = async () => {
    if (!formData.userEmail || !formData.contact.name || !formData.userType) {
      if (onNotification) {
        onNotification(
          'Please fill in all required fields (Email, Name, and Type)',
          'error'
        );
      }
      return;
    }

    const payload = {
      contact: {
        address: formData.contact.address || '',
        govEmail: formData.contact.govEmail || '',
        name: formData.contact.name || '',
        personalPhone: formData.contact.personalPhone || '',
        workEmail: formData.contact.workEmail || '',
        workPhone: formData.contact.workPhone || '',
      },
      roles: prepareRolesForPayload(formData.roles),
      userEmail: formData.userEmail || '',
      userType: formData.userType || '',
    };

    try {
      await postUser({ handlersCreateUserRequest: payload }).unwrap();
      setDialogOpen(false);

      setNewUser({
        userName: '',
        userEmail: '',
        roles: [],
      });

      if (onNotification) {
        onNotification('User successfully created', 'success');
      }
    } catch (error: unknown) {
      console.error('Error creating user:', error);
      if (onNotification) {
        const err = error as { data?: { details?: string } };
        onNotification(`Error creating user: ${err.data?.details}`, 'error');
      }
    }
  };

  // Row click handler for selection
  const handleRowClick = (params: GridRowParams<HandlersUserDetailResponse>) => {
    if (selectedUser?.id !== params.row.id) {
      setSelectedUser(params.row);
      setParentSelectedUser(params.row);
    }
  };

  // Row class for selection highlighting
  const getRowClassName = (
    params: GridRowClassNameParams<HandlersUserDetailResponse>
  ) => {
    return params.row.id === selectedUser?.id ? 'selected-project-row' : '';
  };

  return (
    <>
      {usersFetching ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 100,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Toolbar row — search (left) + Add User (right). The inline
              Name/Email fields are gone; the modal collects them. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <TextField
              size="small"
              placeholder="Search users by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, maxWidth: 360 }}
            />
            <Tooltip
              title={
                <React.Fragment>
                  <strong>Add New User</strong>
                  <br />
                  Default permissions will include:
                  <br />
                  - View Deliverable
                  <br />
                  - Download Deliverable
                  <br />
                  - View a Project
                </React.Fragment>
              }
              placement="top"
              arrow
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddUser}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  backgroundColor: '#1F4D83',
                  '&:hover': {
                    backgroundColor: '#163C66',
                  },
                  color: '#FFFFFF',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                }}
              >
                Add User
              </Button>
            </Tooltip>
          </Box>

          {/* DataGrid */}
          <StyledDataGridPro
            rows={filteredUsers}
            columns={columns}
            loading={usersFetching}
            getRowId={(row) => row.id || row.userEmail || ''}
            onRowClick={handleRowClick}
            getRowClassName={getRowClassName}
            pagination
            pageSizeOptions={[...TABLE_PAGE_SIZE_OPTIONS]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              // Default sort: Name A → Z.
              sorting: { sortModel: [{ field: 'userName', sort: 'asc' }] },
            }}
            disableRowSelectionOnClick
            slotProps={{
              pagination: {
                showFirstButton: true,
                showLastButton: true,
              },
            }}
            sx={{ flex: 1 }}
          />
        </Paper>
      )}

      {/* Add User Dialog */}
      <AddUserDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        formData={formData}
        onFormChange={handleFormChange}
        onSubmit={handleSubmitUser}
      />

      {/* Delete User Dialog */}
      <DeleteUserDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        userToDelete={userToDelete}
        onConfirm={confirmDeleteUser}
        getRolesString={getRolesString}
      />
    </>
  );
}
